import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Star, ChevronDown, CheckCircle2, X, PenLine } from 'lucide-react';
import { reviews as seedReviews } from '../mock';
import SectionHeader from './SectionHeader';
import useReveal from '../hooks/useReveal';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from '../hooks/use-toast';

const parseDate = (s) => {
  if (!s) return new Date(0);
  if (s.includes('T')) return new Date(s);
  const [d, m, y] = String(s).replace(',', '').split(' ');
  const months = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
  return new Date(parseInt(y, 10), months[m] ?? 0, parseInt(d, 10));
};

function ReviewForm({ onClose, onSubmitted }) {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState('');
  const [product, setProduct] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 10) {
      toast({ title: 'Write a little more', description: 'Please use at least 10 characters.', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const data = await api('/reviews', {
        method: 'POST',
        body: { rating, text: text.trim(), product: product.trim() },
      });
      toast({ title: 'Thank you', description: 'Your review is now live.' });
      onSubmitted?.(data.review);
      onClose?.();
    } catch (err) {
      toast({ title: 'Could not post review', description: err.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
    >
      <button type="button" className="absolute inset-0 bg-black/50" aria-label="Close" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-[#F5EFE6] border border-[#E5DCC9] shadow-2xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#8a6f2d]">Verified Voices</p>
            <h3 className="font-serif-display text-2xl text-[#1A2332]">Write a review</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 touch-manipulation" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/60 mb-2">Your rating</p>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(n)}
                  className="p-0.5 touch-manipulation"
                  aria-label={`${n} stars`}
                >
                  <Star
                    className={`w-7 h-7 ${
                      n <= (hover || rating) ? 'fill-[#C9A961] text-[#C9A961]' : 'text-[#E5DCC9]'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/60">Fragrance (optional)</span>
            <input
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder="e.g. Savage Edge"
              className="mt-1.5 w-full bg-white border border-[#E5DCC9] px-3 py-2.5 text-sm outline-none focus:border-[#C9A961]"
            />
          </label>
          <label className="block">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/60">Your review</span>
            <textarea
              required
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, 600))}
              placeholder="Share how it smells, how long it lasts, and when you wear it…"
              className="mt-1.5 w-full bg-white border border-[#E5DCC9] px-3 py-2.5 text-sm outline-none focus:border-[#C9A961] resize-none"
            />
            <span className="text-[11px] text-[#1A2332]/45">{text.length}/600</span>
          </label>
          <button
            type="submit"
            disabled={busy}
            className="w-full bg-[#1A2332] text-[#F5EFE6] py-3.5 text-[11px] tracking-[0.25em] uppercase disabled:opacity-60"
          >
            {busy ? 'Posting…' : 'Post review'}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default function Reviews() {
  const { isLoggedIn, user, loading } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(6);
  const [filter, setFilter] = useState('Most recent');
  const [liveReviews, setLiveReviews] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [ref, visible] = useReveal();
  const filters = ['Most recent', 'Highest rating', 'Lowest rating', 'Most helpful'];

  useEffect(() => {
    api('/reviews', { auth: false })
      .then((d) => setLiveReviews(d.reviews || []))
      .catch(() => setLiveReviews([]));
  }, []);

  const allReviews = useMemo(() => {
    const seeded = seedReviews.map((r, i) => ({
      ...r,
      id: `seed_${i}`,
      verified: true,
      createdAt: parseDate(r.date).toISOString(),
    }));
    const live = liveReviews.map((r) => ({
      ...r,
      verified: r.verified !== false,
    }));
    const liveTexts = new Set(live.map((r) => r.text.trim().toLowerCase()));
    return [...live, ...seeded.filter((r) => !liveTexts.has(r.text.trim().toLowerCase()))];
  }, [liveReviews]);

  const sortedReviews = useMemo(() => {
    const arr = [...allReviews];
    switch (filter) {
      case 'Highest rating':
        return arr.sort(
          (a, b) => b.rating - a.rating || parseDate(b.date || b.createdAt) - parseDate(a.date || a.createdAt)
        );
      case 'Lowest rating':
        return arr.sort(
          (a, b) => a.rating - b.rating || parseDate(b.date || b.createdAt) - parseDate(a.date || a.createdAt)
        );
      case 'Most helpful':
        return arr.sort((a, b) => b.text.length - a.text.length);
      case 'Most recent':
      default:
        return arr.sort((a, b) => parseDate(b.date || b.createdAt) - parseDate(a.date || a.createdAt));
    }
  }, [filter, allReviews]);

  const shown = sortedReviews.slice(0, count);
  const avg =
    allReviews.length > 0
      ? (allReviews.reduce((s, r) => s + (r.rating || 0), 0) / allReviews.length).toFixed(1)
      : '4.8';

  const startWrite = () => {
    if (loading) return;
    if (!isLoggedIn) {
      toast({
        title: 'Sign in to review',
        description: 'Create an account or sign in to share your verified voice.',
      });
      navigate('/login', { state: { from: '/#reviews' } });
      return;
    }
    if (user?.role === 'admin') {
      toast({
        title: 'Customer accounts only',
        description: 'Please sign in with a customer account to leave a review.',
      });
      return;
    }
    setOpenForm(true);
  };

  return (
    <section
      id="reviews"
      ref={ref}
      style={{ scrollMarginTop: '100px' }}
      className={`max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-12 sm:py-14 md:py-24 transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="flex flex-col items-center mb-6 sm:mb-8 px-1">
        <SectionHeader
          eyebrow="Verified Voices"
          title="Real Reviews"
          subtitle="What our patrons say — signed-in customers can share their experience."
        />
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3 mb-6 sm:mb-8">
        <div className="flex items-center gap-2 bg-white border border-[#E5DCC9] rounded-full px-4 sm:px-5 py-2 shadow-sm max-w-full">
          <span className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-[#C9A961] text-[#C9A961]" />
            ))}
          </span>
          <span className="font-serif-display text-base sm:text-lg text-[#1A2332]">{avg}</span>
          <span className="text-[11px] sm:text-xs text-[#1A2332]/60">| {allReviews.length} reviews</span>
          <CheckCircle2 className="w-4 h-4 text-[#8a6f2d] ml-1 flex-shrink-0" />
        </div>
      </div>

      <div
        className="flex gap-2 mb-8 sm:mb-10 overflow-x-auto no-scrollbar -mx-3 px-3 sm:mx-0 sm:px-0 sm:flex-wrap sm:justify-center"
        data-testid="review-filters"
      >
        {filters.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => {
              setFilter(f);
              setCount(6);
            }}
            data-testid={`review-filter-${f.replace(/\s+/g, '-').toLowerCase()}`}
            className={`flex-shrink-0 px-3.5 sm:px-4 py-2 border text-[11px] sm:text-xs tracking-wide rounded-full transition touch-manipulation ${
              filter === f
                ? 'bg-[#1A2332] text-[#F5EFE6] border-[#1A2332]'
                : 'border-[#E5DCC9] text-[#1A2332]/70 hover:bg-[#EFE6D6]'
            }`}
          >
            {f}
          </button>
        ))}
        <button
          type="button"
          onClick={startWrite}
          className="flex-shrink-0 px-3.5 sm:px-4 py-2 bg-[#C9A961] text-[#1A2332] text-[11px] sm:text-xs tracking-wide rounded-full inline-flex items-center gap-1.5 hover:bg-[#b8975a] transition touch-manipulation"
        >
          <PenLine className="w-3.5 h-3.5" />
          {isLoggedIn && user?.role !== 'admin' ? 'Write A Review' : 'Sign in to Review'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="review-list">
        {shown.map((r) => (
          <div
            key={r.id || `${r.name}-${r.date}-${r.text.slice(0, 12)}`}
            className="bg-white/70 backdrop-blur border border-[#E5DCC9] p-5 sm:p-6 rounded-md hover:shadow-lg transition-all relative"
          >
            {r.verified && (
              <div className="absolute top-4 right-4 text-[10px] tracking-[0.2em] uppercase text-[#8a6f2d] flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Verified
              </div>
            )}
            <div className="flex gap-0.5 mb-3">
              {Array.from({ length: r.rating }).map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-[#C9A961] text-[#C9A961]" />
              ))}
            </div>
            {r.product ? (
              <p className="text-[10px] tracking-[0.15em] uppercase text-[#1A2332]/45 mb-2">{r.product}</p>
            ) : null}
            <p className="text-sm text-[#1A2332]/85 leading-relaxed mb-4 min-h-0 sm:min-h-[70px] pr-10">
              “{r.text}”
            </p>
            <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#E5DCC9]">
              <span className="font-serif-display text-[15px] text-[#1A2332] truncate">{r.name}</span>
              <span className="text-[11px] text-[#1A2332]/50 flex-shrink-0">{r.date}</span>
            </div>
          </div>
        ))}
      </div>
      {count < sortedReviews.length && (
        <div className="text-center mt-12">
          <button
            type="button"
            onClick={() => setCount((c) => c + 6)}
            className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#1A2332] text-[#1A2332] text-[11px] tracking-[0.3em] uppercase hover:bg-[#1A2332] hover:text-[#F5EFE6] transition"
          >
            Show More Reviews <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      )}

      {openForm && (
        <ReviewForm
          onClose={() => setOpenForm(false)}
          onSubmitted={(review) => {
            setLiveReviews((list) => [review, ...list]);
            setFilter('Most recent');
            setCount(6);
          }}
        />
      )}
    </section>
  );
}
